import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { tenMinutes } from '~constants/constants'
import {
  FilterParamNameEnum,
  StatusFilterOptionValue,
} from '~pages/pages-behind-login/vessels-page/vessels-page.types'
import { apiBase } from '~utils/base-url'
import {
  getFeatureToggle,
  harryPotterThemeSupportEnabled,
} from '~utils/feature-toggles'
import { useFetch } from '~utils/fetch-request'
import type { TrackFieldValue, VesselCertificate } from '~types/vessel.types'

export interface GetVesselsResItem {
  vesselName: string
  imoNumber: string
  pool: string
  poolPartner?: string
  targetDate: Date
  status: StatusFilterOptionValue
  progress: number
  tcin?: Tcin[]
  expiredCertificates?: VesselCertificate[]
  missingCertificates?: VesselCertificate[]
  expiringCertificatesWithin90Days?: VesselCertificate[]
  certificatesWithUpcomingSurvey?: VesselCertificate[]
  currentVoyage: CurrentVoyage
  vesselId: string
  vesselCode: string
  poolPartnerName?: string
  deliveryDate?: Date
  redeliveryDate?: Date
  onboardedDate?: Date
  archivedAt?: string
  archivedReason?: string
  technicallyManaged?: boolean
  track?: TrackFieldValue
}

interface CurrentVoyage {
  status?: CurrentVoyageStatus
  chartererShortName?: string
  department?: string
  ballastPort?: string
  loadPort?: string
  dischargePort?: string
}

export enum CurrentVoyageStatus {
  Commenced = 'Commenced',
  Scheduled = 'Scheduled',
}

interface Tcin {
  deliveryDateLocal: Date
  redeliveryDateLocal: Date
  counterpartyShortName: string
  status: TcinStatus
  vesselCode: string
}

export enum TcinStatus {
  Delivered = 'Delivered',
  Redelivered = 'Redelivered',
}

interface useGetVesselsProps {
  poolName?: string
  vesselName?: string
  status?: StatusFilterOptionValue
  track?: string
  showArchived?: boolean
  queryDisabled?: boolean
  easterEgg?: boolean
  technicallyManaged?: boolean
}

export function useGetVessels(props: useGetVesselsProps = {}) {
  const fetchRequest = useFetch<GetVesselsResItem[]>()
  const [searchParams, setSearchParams] = useSearchParams()
  const harryPotterThemeParam = searchParams.get(
    FilterParamNameEnum.harryPotterTheme,
  )
  const showArchived = props.showArchived

  useEffect(() => {
    if (showArchived && (props.track === 'all' || !props.track)) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.delete('show-archived')
        return newParams
      })
    }
  }, [showArchived, props.track, setSearchParams])

  const url = constructUrl({ ...props, easterEgg: !!harryPotterThemeParam })
  return useQuery({
    queryKey: [
      '/vessels',
      {
        poolName: props.poolName,
        status: props.status,
        track: props.track,
        showArchived,
        technicallyManaged: props.technicallyManaged,
      },
    ],
    queryFn: () => fetchRequest(`${apiBase()}${url}`),
    staleTime: tenMinutes,
    enabled: !props?.queryDisabled,
  })
}

function constructUrl({
  poolName,
  vesselName,
  status,
  track,
  easterEgg,
  showArchived,
  technicallyManaged,
}: useGetVesselsProps = {}) {
  let url = '/vessels'
  const params = new URLSearchParams()

  if (poolName) {
    params.append('pool', poolName)
  }

  if (vesselName) {
    params.append('vesselName', vesselName)
  }

  if (status) {
    params.append('status', status)
  }

  if (easterEgg && getFeatureToggle(harryPotterThemeSupportEnabled)) {
    params.append('easteregg', 'true')
  }

  if (track && track !== 'all') {
    params.append('track', track)
  }

  if (showArchived) {
    params.append('status', 'archived')
  }

  if (technicallyManaged) {
    params.append('technicallyManaged', 'true')
  }

  if (params.toString()) {
    url += `?${params.toString()}`
  }

  return url
}
